import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import type { MagicLinkErrorCode } from '@identity/application/errors';
import { IdentityWizardLayout, LinkErrorScreen } from '@identity/presentation/components/base';
import type { LinkErrorScreenCopy } from '@identity/presentation/components/base';
import { SIGN_IN_ROUTE } from '@identity/presentation/constants';

import { NewPasswordView } from './NewPasswordView';
import { PasswordResetDoneScreen } from './PasswordResetDoneScreen';

/**
 * The second half of the recovery flow: write the password, then confirm.
 *
 * It reads no requirement map, and that is deliberate. The purpose inside the token already says
 * what to do here and it is one single thing; fetching the map would let this screen derive steps
 * that are not its own — a phone, a consent — and turn a recovery into a half finished verification
 * authorized by a token issued for something else. Whatever is still pending belongs to the gate,
 * which collects it at the next sign in.
 *
 * A dead link takes the whole screen instead of a line under a button, because at that point there
 * is nothing left to retry and the only real way out is asking for another link from the login.
 */
export const PasswordResetWizard = () => {
  const { t } = useTranslation('identity');
  const history = useHistory();
  const [isDone, setIsDone] = useState(false);
  const [linkErrorCode, setLinkErrorCode] = useState<MagicLinkErrorCode | null>(null);

  const onSignIn = () => history.push(SIGN_IN_ROUTE);

  if (linkErrorCode) {
    const expiredCopy: LinkErrorScreenCopy = {
      body: t('recovery.linkExpired.body'),
      title: t('recovery.linkExpired.title'),
    };

    return (
      <LinkErrorScreen
        code={linkErrorCode}
        copy={linkErrorCode === 'LINK_EXPIRED' ? expiredCopy : undefined}
        onSignIn={onSignIn}
      />
    );
  }

  return (
    <IdentityWizardLayout>
      {isDone ? (
        <PasswordResetDoneScreen />
      ) : (
        <NewPasswordView onLinkError={setLinkErrorCode} onSaved={() => setIsDone(true)} />
      )}
    </IdentityWizardLayout>
  );
};
